/**
 * Attention — the landing page for Compliance Evidence.
 *
 * Answers the one question the product needs to answer at a glance: "is my compliance evidence in
 * order, and if not, what specifically do I need to do?" Nothing in the previous UI answered it —
 * seven tools sat at equal weight and the user had to remember which one to check.
 *
 * Two states matter and are deliberately distinct:
 *   - nothing to do        -> an explicit "Everything is current". A blank page reads as broken.
 *   - something unknown    -> named as unchecked. See the note in services/ceAttention.js: on this
 *                             screen above all others, an unreachable site absorbed into a calm
 *                             green state is the failure mode to design against.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorDetail } from '../services/api';
import { fetchAttentionShared } from '../services/ceAttention';

function Row({ testId, tone, title, detail, action }) {
  const border = tone === 'review' ? 'border-amber-700' : 'border-gray-700';
  return (
    <div
      data-testid={testId}
      className={`bg-gray-800 rounded-xl border ${border} p-5 flex items-start justify-between gap-4`}
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="text-xs text-gray-400 mt-1">{detail}</div>
      </div>
      {action}
    </div>
  );
}

export default function CEAttention() {
  const [state, setState] = useState({ items: [], unchecked: [], loading: true });
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await fetchAttentionShared();
      if (!mounted.current) return;
      setState({ ...result, loading: false });
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Could not load what needs attention.'));
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { items, unchecked, loading } = state;
  // "Everything is current" is claimed ONLY when every site was successfully checked and came back
  // clean. If any site is unknown, the calm state is not earned and must not be shown.
  const allClear = !loading && !error && items.length === 0 && unchecked.length === 0;

  return (
    <div className="bg-gray-900 text-gray-100 p-6 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Attention</h1>
          <p className="text-sm text-gray-400 mt-1">
            Anything across your sites that needs a person right now.
          </p>
        </div>

        {loading && <p className="text-xs text-gray-500">Checking your sites…</p>}

        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {allClear && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 text-center">
            <div className="text-sm font-semibold text-white">Everything is current</div>
            <p className="text-xs text-gray-400 mt-2">
              No packets are awaiting your review.
            </p>
          </div>
        )}

        {items.map((item) => (
          <Row
            key={item.key}
            testId={`attention-review-${item.jobId}`}
            tone="review"
            title={`${item.siteName} — packet awaiting your review`}
            detail={item.summary}
            action={(
              <Link
                to={`/ce/review?site=${item.siteId}`}
                className="shrink-0 px-4 py-2 text-sm rounded-lg bg-purple-700 hover:bg-purple-600 text-white font-medium transition-colors"
              >
                Review
              </Link>
            )}
          />
        ))}

        {unchecked.length > 0 && (
          <div
            data-testid="attention-unchecked"
            className="bg-amber-900/30 border border-amber-700 rounded-lg px-4 py-3 text-sm text-amber-300"
          >
            <div className="font-semibold">Some sites could not be checked</div>
            <ul className="mt-2 space-y-1 text-xs">
              {unchecked.map((u, i) => (
                <li key={`${u.site.id}-${i}`}>
                  {u.site.site_name} — {u.reason} could not be checked. Anything waiting there is
                  not shown above.
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
